// src/client/modules/c64-speed-guard.js
// C64 speed changes and tracking (spec 4.8, I6). Leaving 1x latches tracking
// off for the session, before the core is touched, so a failed change or a
// later definition load cannot re-arm it. Tracked games confirm first. Only
// one confirm can be pending. In the classic layout the select is disabled
// while it is open, with focus on the confirm's Cancel button; in the room
// layout it stays enabled (the room closes a confirm whose trigger is
// disabled) and the room's modal layer makes it inert, and every room close
// other than Confirm is a cancel.
// Eager (main.js imports it; game-keys.js reaches it through a main.js
// delegate instead): never imports main.js or anything under
// src/client/game-room/.
import * as diskPicker from './disk-picker.js';
import { showC64SpeedConfirm, hideC64SpeedConfirm } from './game-actions.js';
import { roomSessionKey, isCurrentSessionKey } from './game-room-gate.js';
import * as cheevoPanel from './cheevo-panel.js';
import { applySessionTrackingLatch } from './c64-tracking.js';
import { isDisplayed } from './focus-trap.js';

const select = () => document.getElementById('c64-speed-select');
const confirmShown = () => document.getElementById('c64-speed-confirm')?.classList.contains('hidden') === false;
const currentSpeed = (app) => String(app.emu?._c64EmulationSpeed || 1);
const restoreSelect = (app) => { const el = select(); if (el) el.value = currentSpeed(app); };

// A classic confirm re-enables the select it disabled. Focus goes back to the
// select only when asked and only while it shows: focusing it takes the
// 'c64-speed' gameplay-input lease, which a closing screen would never give back.
function release(app, pending, { focus = true } = {}) {
  const el = select();
  if (!el) return;
  restoreSelect(app);
  if (!pending?.classic) return;
  el.disabled = false;
  if (focus && isDisplayed(el)) el.focus();
}

/** Sets the session latch and turns tracking off through c64-tracking.js's
 * one latch rule (a tracked game's session gets the 'speed' reason, whose
 * notice the HUD and the room's Achievements tab show). Without an engine yet,
 * the next engine load applies the latch. */
export function latchC64Speed(app) {
  const alreadyOff = app._achievementsDisabledThisSession === true;
  app._c64SpeedLatched = true;
  if (app.cheevosEngine) applySessionTrackingLatch(app);
  if (alreadyOff || app._achievementsDisabledThisSession !== true) return;
  app._updateAchievementsHUD?.();
  cheevoPanel.renderVisibleRoomList?.(app);
  app._flash?.('Achievements disabled for this session');
}

function apply(app, value) {
  if (Number(value) !== 1) latchC64Speed(app);
  let result;
  try {
    result = diskPicker.setC64EmulationSpeed(app, Number(value));
  } catch (error) {
    console.warn('[c64-speed] speed change failed', error);
  } finally {
    restoreSelect(app);
  }
  // disk-picker returns false only after telling the player why (its more
  // specific flash); a throw or any other result told nobody.
  if (result !== true && result !== false) app._flash?.('Could not change the speed');
  return result === true;
}

export function requestC64Speed(app, value) {
  if (app._c64SpeedPending && !confirmShown()) cancelC64Speed(app); // hidden by other code
  if (app._c64SpeedPending) { restoreSelect(app); return false; }
  if (String(Number(value)) === currentSpeed(app)) return false;
  const tracked = !app._achievementsDisabledThisSession && app._gameHasTrackedProgress?.();
  if (Number(value) !== 1 && tracked) {
    const pending = { value: Number(value), emu: app.emu, key: roomSessionKey(app), classic: false };
    app._c64SpeedPending = pending;
    restoreSelect(app);
    const outcome = showC64SpeedConfirm(app);
    if (outcome === undefined) {
      // No room presented it. A confirm that could not show has nothing to
      // answer it, so the request resolves as a cancel at once.
      if (!confirmShown()) { cancelC64Speed(app); return false; }
      pending.classic = true;
      const el = select();
      // Focus moves into the confirm before the select is disabled: disabling
      // the focused select would drop focus to <body> (WCAG 2.4.3), and
      // browsers need not fire blur for it, so its 'c64-speed' gameplay-input
      // lease (released on blur) would never be given back. release() returns
      // focus to the select afterwards, except on teardown.
      document.getElementById('c64-speed-confirm-cancel')?.focus();
      if (el && document.activeElement === el) el.blur(); // no Cancel button to take focus
      if (el) el.disabled = true;
    } else {
      Promise.resolve(outcome).then((result) => {
        if (result !== 'confirmed' && app._c64SpeedPending === pending) cancelC64Speed(app);
      }, () => { if (app._c64SpeedPending === pending) cancelC64Speed(app); });
    }
    return false;
  }
  return apply(app, value);
}

export function confirmC64Speed(app) {
  const pending = app._c64SpeedPending;
  app._c64SpeedPending = null;
  hideC64SpeedConfirm(app);
  release(app, pending);
  if (!pending || pending.emu !== app.emu || !isCurrentSessionKey(app, pending.key)) return false;
  const changed = apply(app, pending.value);
  release(app, pending);
  return changed;
}

/** Cancel, Escape, any room close but Confirm, and teardown. Idempotent.
 * Session teardown passes { focus: false }: the screen is closing, so focus
 * (and the select's gameplay-input lease) never moves into it. */
export function cancelC64Speed(app, { focus = true } = {}) {
  const pending = app._c64SpeedPending;
  app._c64SpeedPending = null;
  hideC64SpeedConfirm(app);
  release(app, pending, { focus });
}
